import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Tooltip,
  Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { useFormError } from '@/providers/FormErrorContext';
import { deleteUser } from '@/actions/user';
import { useAuth, useAuthDispatch } from '@/providers/AuthContext';
import { useApi } from '@/providers/ApiContext';
import { USER_LOGOUT } from '@/providers/actionTypes';
import type { OnboardingStep } from '@/types';

interface NewUserDesktopWizardProps {
  steps: OnboardingStep[];
  onNext: () => Promise<void>;
}

function NewUserDesktopWizard({ steps, onNext }: NewUserDesktopWizardProps) {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [skipped, setSkipped] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState<boolean>(false);
  const [backLoading, setBackLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const formError = useFormError();
  const api = useApi();
  const auth = useAuth();
  const auth_dispatch = useAuthDispatch();

  const isStepSkipped = (step: number): boolean => {
    return skipped.has(step);
  };

  const handleNext = async () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }
    setLoading(true);
    await onNext();
    setLoading(false);
    if (activeStep === steps.length - 1) {
      navigate('/');
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      setSkipped(newSkipped);
    }
  };

  const handleDelete = async () => {
    if (!api) return;
    setBackLoading(true);
    await deleteUser(api);
    auth_dispatch({ type: USER_LOGOUT });
    if (auth.user_manager) {
      await auth.user_manager.signoutRedirect();
    }
    setBackLoading(false);
  };

  const handleBack = async () => {
    if (activeStep === 0) {
      await handleDelete();
      navigate('/'); //delete user
    }

    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkip = () => {
    if (!steps[activeStep].optional) {
      // You probably want to guard against something like this,
      // it should never occur unless someone's actively trying to break something.
      throw new Error("You can't skip a step that isn't optional.");
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Stepper activeStep={activeStep}>
        {steps.map((step, index) => {
          return (
            <Step key={index}>
              <StepLabel>{step.name}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      {steps[activeStep].component}
      <Divider />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          pt: 2,
        }}
      >
        <LoadingButton
          loading={backLoading}
          color='inherit'
          onClick={handleBack}
          sx={{ mr: 1 }}
        >
          Back
        </LoadingButton>
        <Box sx={{ flex: '1 1 auto' }} />
        {steps[activeStep].optional && (
          <Button color='inherit' onClick={handleSkip} sx={{ mr: 1 }}>
            Skip
          </Button>
        )}
        <Tooltip
          title={Boolean(formError.error) ? 'Fix form errors to continue.' : ''}
        >
          <span>
            <LoadingButton
              onClick={handleNext}
              loading={loading}
              disabled={Boolean(formError.error)}
            >
              {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
            </LoadingButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default NewUserDesktopWizard;
